<template>
  <v-dialog v-model="showNewNbnOrder" width="1200" persistent>
    <v-card>
      <div id="holder"></div>

      <v-banner sticky class="primary custom-padding py-0">
        <v-row class="text-left pb-0">
          <v-col cols="12" md="3" lg="2" class="py-0 pt-5">
            <span class="white--text custom-title">Create NBN Order</span>
          </v-col>
          <v-col cols="12" md="4" lg="4" class="pb-0">
            <ValidationProvider
              name="Wholesaler"
              rules="required"
              v-slot="{ errors }"
            >
              <v-autocomplete
                :items="wholesalerData"
                label="Wholesaler"
                outlined
                placeholder="Select wholesaler"
                :error-messages="
                  !wholesalerAccount ? 'Wholesaler is required' : errors
                "
                :error="!wholesalerAccount"
                :item-text="setCompany"
                item-value="accountNumber"
                dense
                dark
                v-model="wholesalerAccount"
                :readonly="wholesalerData.length === 1"
                :disabled="isOriginal"
                @change="wholesalerChanged"
              ></v-autocomplete>
            </ValidationProvider>
            <v-spacer></v-spacer>
          </v-col>
        </v-row>
      </v-banner>
      <v-card-text v-if="isOriginal" class="py-3 px-4">
        <nbn-order-primary-actions
          @service-restoration="forwardServiceRestoration"
          @disconnect="forwardDisconnect"
          @edit="forwardEdit"
          @correction="forwardCorrection"
          @resume="forwardResume"
          @reversal="forwardReversal"
          @withdraw="forwardWithdraw"
          @remove-configuration="forwardRemoveConfiguration"
        />
      </v-card-text>
      <v-divider class="mt-6 mb-4"></v-divider>
      <v-card-text class="pb-0" >
        <div v-if="isOriginal && assuranceEnabled">
          <nbn-service-diagnostics
            :AvcId="nbnOrder.avcId"
            :order-type="nbnOrder.productType"></nbn-service-diagnostics>
        </div>
      </v-card-text>
      <v-card-text v-if="featureFlags.sog5391 && tickets.length > 0" class="pb-0" >
        <v-spacer></v-spacer>
          <v-toolbar>
          <v-toolbar-title>Existing Service Restoration Tickets: {{tickets.join(",")}}</v-toolbar-title>
            <v-spacer></v-spacer>
          </v-toolbar>
      </v-card-text>
      <v-divider class="mt-4 mb-8"></v-divider>
      <v-card-text class="pb-0" >
        <div v-if="isOriginal && isUsageFeatureOn && getRadiusUsername != null && getRadiusUsername !== ''">
          <v-col>
            <div class="d-flex justify-space-between align-center mb-3">
              <span class="custom-title">Radius Auth Session and Data Usage</span>
              <div class="d-flex align-center">
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="startDate"
                      label="Start Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      dense
                      outlined
                      clearable
                      class="mr-2"
                      style="max-width: 200px;"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="startDate"
                    @input="startDateMenu = false"
                  ></v-date-picker>
                </v-menu>
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="endDate"
                      label="End Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      dense
                      outlined
                      clearable
                      class="mr-2"
                      style="max-width: 200px;"
                      :error-messages="dateRangeError"
                      :error="!isDateRangeValid"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="endDate"
                    @input="endDateMenu = false"
                  ></v-date-picker>
                </v-menu>
                <v-btn 
                  class="primary" 
                  @click="exportRadiusAuthData"
                  :loading="isExporting"
                  :disabled="isExporting || !isDateRangeValid"
                >
                  Export Data
                </v-btn>
              </div>
            </div>
          </v-col>
          <session-auth-detail
            :username="getRadiusUsername"
            :nbnOrderId="getNbnOrderId"
            :startDate="startDate"
            :endDate="endDate"
          >
          </session-auth-detail>
        </div>
      </v-card-text>

      <v-card-text class="pb-0" ref="calcHeight">
        <v-row>
          <v-col cols="12" md="8" class="pt-2">
            <div id="holder"></div>
            <qualification-form-nbn
              ref="qualificationFormNbn"
              :isOriginal="isOriginal"
              @showResultSection="showResultSection"
              @toggleInput="toggleInput"
              @changed="changed"
            ></qualification-form-nbn>
            <qualification-result-nbn
              ref="qualificationResultNbn"
              class="mt-5"
              :showButtons="true"
              :showLocIdInput="showLocIdInput"
              :queryAddress="queryAddress"
              :addressState="addressState"
              :hideOtherButtons="false"
              :withData="true"
              :selected="selected"
              @createOrder="setNewOrder"
              @showResultSection="assignResult"
              :hasMatch="hasMatch"
              v-bind:wholesalerAccount="wholesalerAccount"
              @partiesLoaded="onPartiesLoaded"
              :requireWholesalerSelection ="requireWholesalerSelection"
            ></qualification-result-nbn>
          </v-col>
          <v-col cols="12" md="4" v-if="!isOriginal">
            <enhanced-sq-nbn></enhanced-sq-nbn>
          </v-col>
        </v-row>
      </v-card-text>

      <ValidationObserver ref="observer" v-slot="{ dirty, invalid }">
        <v-card-text v-show="!sqRejected && (runSq || isOriginal) ">
          <v-divider class="mt-4 mb-8"></v-divider>
          <order-qualification-form
            ref="orderQualificationFormNbn"
            :isNewOrder="true"
            :new-order="newOrder"
            :showNewNbnOrder="showNewNbnOrder"
            :showPrimaryActionsAtTop="false"
            v-bind:wholesalerAccount="wholesalerAccount"
            @showOrderResultSection="showOrderResultSection"
            @showCreatedOrderResult="showCreatedOrderResult"
            @toggleInput="toggleInput"
            @orderTypeChanged="orderTypeChanged"
            @closeDialog="close()"
            :filteredParties ="parties"
          ></order-qualification-form>
          <order-qualification-result
            ref="orderQualificationResultNbn"
            class="mt-5"
          ></order-qualification-result>
        </v-card-text>

        <div class="fixed-bar rtl primary py-4">
          <v-btn @click="close()" class="mr-4">Close</v-btn>
        </div>
      </ValidationObserver>
    </v-card>
  </v-dialog>
</template>

<script>
import { Order } from "@/models/Order";
import { nbnSqFailureMessage } from "@/utils/nbnSqDisplay";
import DataUsageDetail from "../orderQualification/data-usage-detail";
import SessionAuthDetail from "../orderQualification/session-auth-detail";
import NbnOrderPrimaryActions from "@/components/orderQualification/nbn-order-primary-actions";

export default {
  name: "create-nbn-order",
  components: { SessionAuthDetail, DataUsageDetail, NbnOrderPrimaryActions },
  props: {
    showNewNbnOrder: { type: Boolean, required: true },
    newOrder: { type: Object, required: true },
    filteredParties: { type: Array, required: false },
    requireWholesalerSelection: { type: Boolean, required: false, default: false },
  },
  data: () => ({
    details: {},
    ntd: [],
    unid: [],
    queryAddress: [],
    showResult: false,
    showLocIdInput: false,
    hasMultipleLocId: true,
    newDate: null,
    showDetails: false,
    selectedWholeSaler: null,
    wholesalerAccount: 0,
    isServiceable: false,
    hasMatch: true,
    addressState: "",
    selected: "Physical Address",
    workforceClass: ["23", "24", "30"],
    isContactValid: false,
    deliveryDetails: {
      address: "",
      name: "",
      company: "",
    },
    isOfferVisible: false,
    primaryAccessTechnology: "",
    fttc: ["30", "31", "32", "33", "34"],
    fttb: ["10", "11", "12", "13"],
    sqRejected: false,
    runSq: false,
    tickets: [],
    parties: [],
    startDate: null,
    endDate: null,
    startDateMenu: false,
    endDateMenu: false,
    isExporting: false,
  }),

  computed: {
    invalidProductErr() {
      let vm = this;
      return vm.$route.path === "/"
        ? `${vm.primaryAccessTechnology} is not included in our services. Can't proceed with the order.`
        : `${vm.primaryAccessTechnology} is not included in our services.`;
    },
    assuranceEnabled() {
      let vm = this;
      return vm.featureFlags.sog3900;
    },
    isFttb() {
      let vm = this;
      if (vm.fttb.includes(vm.formattedSQResult.serviceabilityClass))
        return true;
      else return false;
    },
    isFttc() {
      let vm = this;
      if (vm.fttc.includes(vm.formattedSQResult.serviceabilityClass))
        return true;
      else return false;
    },
    disableCreate() {
      let vm = this;
      if (vm.isCustomWorkforce) {
        return (
          !vm.showDetails ||
          !vm.isServiceable ||
          !vm.isContactValid ||
          !vm.isOfferVisible
        );
      } else return !vm.showDetails || !vm.isServiceable || !vm.isOfferVisible;
    },
    isCustomWorkforce() {
      let vm = this;
      return vm.workforceClass.includes(
        vm.formattedSQResult.serviceabilityClass
      );
    },
    getRadiusUsername() {
      let vm = this;
      if(vm.nbnOrder.radiusUsername != null && vm.nbnOrder.radiusUsername !== "")
      {
        return vm.nbnOrder.radiusUsername;
      } else if(vm.nbnOrder.existingRadiusUsername != null && vm.nbnOrder.existingRadiusUsername !== "")
      {
        return vm.nbnOrder.existingRadiusUsername;
      } else {
        return "";
      }
    },
    getNbnOrderId() {
      let vm = this;
      return vm.nbnOrder.nbnOrderId;
    },
    isUsageFeatureOn() {
      let vm = this;
      return vm.featureFlags && vm.featureFlags.sog5110;
    },
    isDateRangeValid() {
      let vm = this;
      if (!vm.startDate || !vm.endDate) {
        return true;
      }
      return new Date(vm.endDate) >= new Date(vm.startDate);
    },
    dateRangeError() {
      let vm = this;
      if (vm.startDate && vm.endDate && !vm.isDateRangeValid) {
        return 'End date must be after or equal to start date';
      }
      return '';
    },
    wholesalerData: function () {
      let vm = this;
      let data = null;

      if (vm.impersonating !== null) {
        if (vm.isUserMember) {
          data = vm.wholesalers;
          if(!vm.isOriginal){
            if(vm.wholesalers.length > 1)
              vm.wholesalerAccount = 0;
            else {
              vm.wholesalerAccount = vm.wholesalers[0].accountNumber;
            }
          }
        } else {
           data = vm.wholesalers.filter(
            (x) => x.oneLoginEmail === vm.impersonating.impersonateEmail
          );
          vm.selectedWholeSaler = data[0];
        }
      } else {
        if (vm.isAdmin) {
          data = vm.wholesalers;

          if (vm.isOriginal)
            vm.selectedWholeSaler = vm.wholesalers.filter(
              (x) =>
                x.accountNumber === vm.selectedOrder.wholesalerAccountNumber
            ).wholesalerAccountNumber;
          else vm.selectedWholeSaler = null;
        } else {
          data = vm.wholesalers.filter(
            (x) => x.oneLoginEmail === vm.user.email
          );
          vm.selectedWholeSaler = data[0];
        }
      }

      return data;
    },
  },
    methods: {
    onPartiesLoaded(parties) {
      let vm = this;
      vm.parties = parties;
    },
    setNewOrder() {
      //
    },
    setTickets(tickets) {
      let vm = this;
      vm.tickets = tickets;
    },
    updateWorkforce(value) {
      let vm = this;
      vm.newOrder.installationWorkforce = value;
    },

    checkLayer() {
      const vm = this;
      if(vm.wholesalerData.length === 1){
        vm.retrieveFeatures(vm.wholesalers[0].accountNumber);
      } else {
        vm.retrieveFeatures(vm.wholesalerAccount);
      }
    },
    retrieveFeatures(accountNumber) {
      let vm = this;
      vm.getFeatures(accountNumber);
    },
    wholesalerChanged() {
      let vm = this;
      vm.getUBCustomers({ id: vm.wholesalerAccount, keyword: "all" });
      vm.checkLayer();
      vm.setUserInternal(vm.wholesalerAccount);
    },
    displayOrder(item, sqResponse) {
      let vm = this;
      vm.showResult = true;
      vm.showLocIdInput = true;
      vm.wholesalerAccount = item.wholesalerAccountNumber;
      vm.displaySqResult(item);
      item.sog6028 = vm.featureFlags.sog6028;
      vm.setNbnOrderDetails(item);

      vm.$nextTick(() => {
        vm.setNbnOrderDetails(item);
        vm.wholesalerChanged();

        if (item.customerAuthReference?.trim()) {
          vm.getFiles(item.customerAuthReference);
        }

        if(vm.$refs.qualification !== undefined)
        {
          vm.$refs.qualification.streetAddress = item.address;
        }
        if(vm.$refs.qualificationResult !== undefined)
        {
          vm.$refs.qualificationResult.displayOrder(item, sqResponse);
        }
        if(vm.$refs.orderDetails !== undefined)
        {
          vm.$refs.orderDetails.displayOrder();
        }
        if(vm.$refs.orderQualificationFormNbn !== undefined)
        {
          vm.$refs.orderQualificationFormNbn.setStaticIp();
        }
      });
    },

    displaySqResult(item) {
      let vm = this;
      vm.showResult = true;
      vm.showLocIdInput = true;
      let sqJson = JSON.parse(item._sqResponse);

      vm.$nextTick(() => {
        vm.$refs.qualificationResultNbn.showSqResult = true;
        vm.$refs.qualificationResultNbn.setFormattedNbnSQResult(sqJson);
        vm.$refs.qualificationResultNbn.result = sqJson;
      });
    },
    displayOffer() {
      let vm = this;
      vm.isOfferVisible = true;
      if (!vm.$refs.orderDetails.showInstallationWorkforce)
        vm.isContactValid = true;
      else vm.isContactValid = false;
    },
    hideBaseOffer() {
      let vm = this;
      vm.$refs.orderDetails.showMore = false;
      vm.isOfferVisible = false;
      vm.isServiceable = false;
    },
    setDeliveryDetails(value) {
      let vm = this;
      if (value) {
        vm.newOrder.deliveryAddress = vm.fullAddress.formatted_address;
        vm.newOrder.deliveryContactName = `${vm.newOrder.customerFirstName} ${vm.newOrder.customerLastName}`;
      } else {
        vm.newOrder.deliveryAddress = "";
        vm.newOrder.deliveryContactName = "";
      }
    },
    updateContact(techDetails) {
      let vm = this;
      vm.newOrder.contactFirstName = techDetails.firstName;
      vm.newOrder.contactLastName = techDetails.lastName;
      vm.newOrder.contactNumber = techDetails.number;
      vm.newOrder.contactEmail = techDetails.email;
      vm.updateOfferContact(techDetails);
      vm.isContactValid = true;
    },
    beforeCreateOrder() {
      const vm = this;
      let errorMessage = "";
      if (vm.disableCreate) {
        errorMessage = "Please check missing fields";
      } else {
        errorMessage = "";
      }

      vm.$refs.observer.validate().then((result) => {
        if (result && !errorMessage.length) {
          vm.createOrder();
        } else {
          vm.setHttpResponse({
            text: errorMessage,
            color: "error",
          });
        }
      });
    },
    createOrder() {
      let vm = this;
      vm.createNewOrder(vm.customizedOfferResult).then((data) => {

        if (data !== null) {
          setTimeout(() => {
            vm.setCustomizedOffer({});
            vm.newOrder = new Order();
            vm.$emit("orderSubmitted");
            vm.setHttpResponse({
              text: `Order submitted and saved.`,
              color: "success",
            });
          }, 500);
        } else {
          setTimeout(() => {
            vm.setHttpResponse({
              text: `Failed. ${data.message}`,
              color: "error",
            });
          }, 500);
        }
      });
    },
    setCompany(details) {
      if (!details) return "";
      const text = details.company || details.name || "";
      return String(text).trim();
    },
    setWholesaler() {
      let vm = this;
      vm.getWholesalers()
        .then((data) => {
          if (!vm.isUserMember || data.length === 1) {
            vm.selectedWholeSaler = data[0];
            if(vm.selectedWholeSaler.is_radius_user === "n")
            {
              const columnIndex = vm.$parent.columnDefs.findIndex(column => column.field === "radiusStatus");
              if (columnIndex !== -1) {
                vm.$parent.columnDefs.splice(columnIndex, 1);
              }
            }
          } else vm.selectedWholeSaler = null;
        })
        .catch(() => {});
    },
    createOffer(order) {
      let vm = this;
      let payload = {
        locationId: vm.locationID,
        customerAuthDate: new Date(),
        orderType: order.orderType.value.toUpperCase(),
        requestedSpeed: order.avcdBandwidthProfile.value
          ? order.avcdBandwidthProfile.value
          : order.avcdBandwidthProfile,
        contractLength: order.contractTermLength,
        customer: {
          firstName: order.customerFirstName,
          lastName: order.customerLastName,
          contactNo: order.customerContactNumber,
          email: order.customerContactEmail,
        },
        ticketId: order.customerReference,
        customerAuthReference: order.customerAuthReference,
        operatorContactEmail: vm.user.email,
        operatorContactNumber: order.contactNumber,
        operatorContactName: `${order.contactFirstName} ${order.contactLastName}`,
        serviceId: "",
        accessLoopId: "N/A",
        fnn: order.fnn,
        wholesalerAccount: vm.wholesalerAccount,
      };

      vm.createCustomisedOffer(payload).then((data) => {
        if (data.data) {
          data.data.baseOffer.serviceFeatures.forEach((x) => {
            switch (x.featureKey) {
              case "NBN_SERVICE_SPEC:LOCATION_ID":
                vm.newOrder.locationId = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:AVCD_BANDWIDTH_PROFILE":
                vm.newOrder.avcdBandwidthProfile =
                  x.value.value === "12/1"
                    ? "Entry Level Bundle"
                    : x.value.value;
                break;
              case "NBN_SERVICE_SPEC:RSP_DELIVERY_CIRCUIT":
                vm.newOrder.rspDeliveryCircuit = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:CUSTOMER_FIRST_NAME":
                vm.newOrder.customerFirstName = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:CUSTOMER_LAST_NAME":
                vm.newOrder.customerLastName = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:CUSTOMER_CONTACT_NUMBER":
                vm.newOrder.customerContactNumber = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:CUSTOMER_AUTH_DATE":
                vm.newOrder.customerAuthDate = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:UNIDSL_POTS_INTERCONNECT":
                vm.newOrder.unidslPotsInterconnect = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:SERVICE_CLASS":
                vm.newOrder.serviceClass = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:CUSTOMER_REFERENCE":
                vm.newOrder.customerReference = x.value.value;
                if (vm.isAdmin || vm.isUserMember)
                  vm.newOrder.customerAuthReference = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:CUSTOMER_AUTH_REFERENCE":
                if (!vm.isAdmin || !vm.isUserMember)
                  vm.newOrder.customerAuthReference = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:CENTRAL_SPLITTER":
                vm.newOrder.centralSplitter = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:SPECIAL_INSTRUCTION":
                vm.newOrder.specialInstruction = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:PORTING_NUMBER":
                vm.newOrder.portingNumber = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:CPI_ID":
                vm.newOrder.cpiId = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:NTD_ID":
                vm.newOrder.ntdId = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:INSTALLATION_WORKFORCE":
                if (vm.isCustomWorkforce)
                  vm.newOrder.installationWorkforce = "";
                else if (vm.isFttc) {
                  if (
                    vm.sqResult.parameters.parameterGroup &&
                    vm.sqResult.parameters.parameterGroup.length > 0
                  ) {
                    let cpi = vm.sqResult.parameters.parameterGroup.filter(
                      (x) => x.groupName === vm.newOrder.cpiId
                    );

                    if (cpi && cpi.length > 0) {
                      let cpiClass = cpi[0].parameter.filter(
                        (x) => x.name === "ServiceabilityClass"
                      )[0].value;

                      if (vm.fttc.includes(cpiClass)) {
                        if (cpiClass === "31" || cpiClass === "32")
                          vm.newOrder.installationWorkforce =
                            "Professional Install";
                        else vm.newOrder.installationWorkforce = "";
                      }
                    }
                  }
                } else if (vm.isFttb) {
                  if (vm.hasFreePort)
                    vm.newOrder.installationWorkforce = "Professional Install";
                  else vm.newOrder.installationWorkforce = "";
                } else vm.newOrder.installationWorkforce = "Not Required";
                break;
              case "NBN_SERVICE_SPEC:CONTACT_NUMBER":
                vm.newOrder.contactNumber = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:CONTACT_EMAIL":
                vm.newOrder.contactEmail = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:DELIVERY_INSTRUCTIONS":
                vm.newOrder.deliveryInstructions = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:CSA_ID":
                vm.newOrder.csaId = x.value.value;
                break;
              case "NBN_SERVICE_SPEC:UNID_PORT_ID":
                vm.newOrder.unidPortId = x.value.value;
                break;
            }
          });

          vm.$refs.orderDetails.showMore = true;
          vm.isOfferVisible = true;
          vm.showDetails = true;
        }
        vm.$refs.orderDetails.checkRequired();
      });
    },
    backToOffer() {
      let vm = this;
      vm.showDetails = false;
      vm.newOrder.customerAuthDate = "";
      vm.newOrder.unidslPotsInterconnect = "";
      vm.newOrder.cpiId = "";
      vm.newOrder.ntdId = "";
      vm.newOrder.unidPortID = "";
      vm.newOrder.installationWorkforce = "";
      vm.newOrder.portingNumber = "";
      vm.newOrder.specialInstruction = "";
      vm.newOrder.deliveryInstructions = "";
    },
    close() {
      let vm = this;
      if(document.getElementById("holder") != null)
      {
        document.getElementById("holder").scrollIntoView({ behavior: "smooth" });
      }
      if(vm.$refs.qualificationFormNbn !== undefined)
      {
        vm.$refs.qualificationFormNbn.selected = "Physical Address";
      }
      vm.queryAddress = [];
      vm.showResult = false;
      vm.$emit("closeNbnOrder");
      vm.setOriginal(false);
      vm.resetProduct();
      vm.resetProductType();
      vm.resetNbnOrderType();
      vm.resetNtdId();
      vm.resetNtdType();
      vm.resetCpiId();
      vm.resetInstallationWorkforce();
      vm.resetFormattedSqNbnResult();
      vm.resetRadiusUsernameResults();
      vm.setOldOrder(false);

      vm.showResult = false;
      vm.showLocIdInput = false;
      vm.sqRejected = false;
      vm.nbnOrder.locationId = "";
      vm.runSq = false;
      vm.tickets = [];
      vm.startDate = null;
      vm.endDate = null;
      vm.isExporting = false;

      vm.$nextTick(() => {
        if (this.$refs.qualificationResultNbn) {
          this.$refs.qualificationResultNbn.showSqResult = false;
        }
      });
    },
    showResultSection(details) {
      let vm = this;
      if (this.$refs.qualificationResultNbn) {
        this.$refs.qualificationResultNbn.showSqResult = false;
      }

      let payload = {};
      vm.$refs.qualificationResultNbn.resetData();
      vm.setOrderType("nbn");

      vm.resetHasNtd();
      let data = {};
      let methodType = "";

      methodType = "NBN_LOCATION_ID";
      if(details.queryAddress.length > 0) data = { LocationId: details.queryAddress[0].locationId };

      payload = {
        addressCheckMethodType: methodType,
        addressValue: data
      };

      if (vm.selected === "Physical Address") {
        vm.hasMatch =
          details.result && details.result.length > 0;
      }

      vm.queryAddress = details.queryAddress;
      vm.streetAddress = details.streetAddress;
      vm.populatedAddress = details.populatedAddress;

      if (
        details.queryAddress.length === 1 &&
        details.queryAddress.length !== 0
      ) {
        vm.showLocIdInput = false;

        vm.serviceQualificationNbn(payload).then((data) => {
          const sq = data && data.data && data.data.sq;
          const restriction = sq && sq.siteRestriction;
          const technology = restriction && restriction.supportingTechnology;
          if (!restriction || !technology) {
            if (!data || data.success !== false) {
              vm.setHttpResponse({
                text: nbnSqFailureMessage(data),
                color: "error"
              });
            }
            return;
          }
          if(restriction.serviceabilityStatus === "Rejected" || technology.primaryAccessTechnology === "Satellite")
          {
            vm.sqRejected = true;
            vm.$refs.qualificationResultNbn.showSqResult = true;
            vm.isServiceable = false;
            return;
          }
          vm.runSq = true;
          const boundaries = restriction.supportingRelatedSiteBoundaries || {};
          vm.$refs.orderQualificationFormNbn.setCsaId(boundaries.CSAId);
          vm.$refs.orderQualificationFormNbn.setServiceClass(technology.serviceabilityClass);
          vm.setFormattedNbnSQResult(data.data);
          vm.setProductType(technology.primaryAccessTechnology
          );
          vm.setNtdId(data.data);
          vm.setNtdType(data.data);
          vm.setCpiId(data.data);

          vm.$refs.qualificationResultNbn.result = data.data;
          vm.$refs.qualificationResultNbn.showSqResult = true;

          if (data.data.sq.id != null && data.data.sq.id != "") {
            vm.$refs.orderQualificationFormNbn.updateLocationId(
              data.data.sq.id
            );
          }
        });
      } else vm.showLocIdInput = true;
    },
    assignResult(details) {
      let vm = this;
      const sq = details && details.sq;
      const restriction = sq && sq.siteRestriction;
      const technology = restriction && restriction.supportingTechnology;
      if (!restriction || !technology) {
        vm.setHttpResponse({
          text: nbnSqFailureMessage(details),
          color: "error"
        });
        return;
      }
      if(vm.$refs.qualificationResultNbn !== undefined)
      {
        vm.$refs.qualificationResultNbn.showSqResult = false;
      }
      vm.runSq = true;

      if(restriction.serviceabilityStatus === "Rejected" || technology.primaryAccessTechnology === "Satellite")
      {
        vm.sqRejected = true;
        vm.$refs.qualificationResultNbn.showSqResult = true;
        vm.isServiceable = false;
        return;
      } else {
        vm.sqRejected = false;
      }

      if (sq.id != null && sq.id != "") {
        vm.$refs.orderQualificationFormNbn.updateLocationId(
          sq.id
        );
      }

      const boundaries = restriction.supportingRelatedSiteBoundaries;
      if (boundaries && boundaries.CSAId != null && boundaries.CSAId != "") {
        vm.$refs.orderQualificationFormNbn.setCsaId(boundaries.CSAId);
        vm.$refs.orderQualificationFormNbn.setServiceClass(
          technology.serviceabilityClass
        );
      }
      if (details.queryAddress != null) {
        vm.queryAddress = details.queryAddress;
        vm.showLocIdInput = true;
      } else {
        vm.$refs.qualificationResultNbn.showSqResult = true;
        vm.$refs.qualificationResultNbn.result = details;
        vm.$refs.qualificationResultNbn.setFormattedNbnSQResult(details);
      }
    },
    toggleInput(state) {
      let vm = this;
      vm.showLocIdInput = state;
      vm.$refs.qualificationResultNbn.hideResult();
    },
    setNewDate(date) {
      let vm = this;
      vm.setNewDate = date;
    },
    changed(selected) {
      let vm = this;

      vm.showLocIdInput = false;
      if(vm.$refs.qualificationResultNbn !== undefined)
      {
        vm.$refs.qualificationResultNbn.addresses = [];
        vm.$refs.qualificationResultNbn.showSqResult = false;
        vm.$refs.qualificationResultNbn.hideResult();
      }
      vm.queryAddress = [];

      vm.hasMatch = true;

      if (selected !== undefined) {
        vm.selected = selected;
      }
    },
    showOrderResultSection(details) {
      let vm = this;
      vm.$refs.orderQualificationResultNbn.showOrderResult = true;
      vm.$refs.orderQualificationResultNbn.result = details;
    },
    showCreatedOrderResult(details) {
      let vm = this;
      vm.close();
    },
    handlePageRefresh(){
      let vm = this;
      vm.close();
    },
    orderTypeChanged() {
      let vm = this;
      vm.showResult = false;
      vm.$refs.orderQualificationResultNbn.showOrderResult = false;
      vm.$refs.orderQualificationResultNbn.result = {};
    },
    setServiceability(value) {
      let vm = this;
      if (value) {
        if (value.includes("Rejected")) vm.isServiceable = false;
        else vm.isServiceable = true;
      } else {
        if (vm.sqResult.serviceability.includes("Rejected"))
          vm.isServiceable = false;
        else vm.isServiceable = true;
      }
    },
    async exportRadiusAuthData(){
      let vm = this;

      if (!vm.isDateRangeValid) {
        vm.setHttpResponse({
          text: 'Invalid date range. End date must be after or equal to start date.',
          color: 'error',
        });
        return;
      }

      vm.isExporting = true;

      try {
        const CHUNK_SIZE = 5000;
        const MAX_RECORDS = 50000;

        const initialPayload = {
          radiusUsername: vm.getRadiusUsername,
          nbnOrderId: vm.getNbnOrderId,
          pageSize: 1,
          pageNumber: 1,
        };

        if (vm.startDate) {
          initialPayload.startDate = vm.startDate;
        }
        if (vm.endDate) {
          initialPayload.endDate = vm.endDate;
        }

        const initialData = await vm.getServiceRadiusAuthDetails(initialPayload);
        const totalRecords = initialData.totalCount || 0;

        if (totalRecords === 0) {
          vm.setHttpResponse({
            text: 'No data available to export',
            color: 'warning',
          });
          return;
        }

        if (totalRecords > MAX_RECORDS) {
          vm.setHttpResponse({
            text: `Dataset is too large (${totalRecords.toLocaleString()} records). Please narrow your date range. Maximum ${MAX_RECORDS.toLocaleString()} records allowed.`,
            color: 'error',
          });
          return;
        }

        vm.setHttpResponse({
          text: `Exporting ${totalRecords.toLocaleString()} records...`,
          color: 'info',
        });

        let allData = [];
        const totalPages = Math.ceil(totalRecords / CHUNK_SIZE);

        for (let page = 1; page <= totalPages; page++) {
          const radiusStatusPayload = {
            radiusUsername: vm.getRadiusUsername,
            nbnOrderId: vm.getNbnOrderId,
            pageSize: CHUNK_SIZE,
            pageNumber: page,
          };

          if (vm.startDate) {
            radiusStatusPayload.startDate = vm.startDate;
          }
          if (vm.endDate) {
            radiusStatusPayload.endDate = vm.endDate;
          }

          const data = await vm.getServiceRadiusAuthDetails(radiusStatusPayload);
          allData = allData.concat(data.items || []);

          if (totalPages > 1) {
            vm.setHttpResponse({
              text: `Exporting... ${Math.round((page / totalPages) * 100)}% complete`,
              color: 'info',
            });
          }
        }

        if (allData.length === 0) {
          vm.setHttpResponse({
            text: 'No data retrieved for export',
            color: 'warning',
          });
          return;
        }

        const csvContent = vm.convertToCsv(allData);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        const dateRangeLabel = vm.startDate || vm.endDate ? `_${vm.startDate || 'start'}_to_${vm.endDate || 'end'}` : '';
        link.setAttribute('download', `${vm.nbnOrder.avcId}_export${dateRangeLabel}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        vm.setHttpResponse({
          text: `Successfully exported ${allData.length.toLocaleString()} records`,
          color: 'success',
        });
      } catch (error) {
        console.error('Export error:', error);
        vm.setHttpResponse({
          text: error.response?.data || 'Failed to export data',
          color: 'error',
        });
      } finally {
        vm.isExporting = false;
      }
    },
    convertToCsv(data) {
       const header = Object.keys(data[0])
        .map(key => this.capitalize(key))
        .join(',') + '\n';
      const rows = data.map(row => Object.values(row).join(',')).join('\n');
      return header + rows;
    },
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    forwardServiceRestoration() {
      this.$refs.orderQualificationFormNbn?.onTriggerServiceRestoration();
    },
    forwardDisconnect() {
      this.$refs.orderQualificationFormNbn?.onTriggerDisconnect();
    },
    forwardEdit() {
      this.$refs.orderQualificationFormNbn?.editForm();
    },
    forwardCorrection() {
      this.$refs.orderQualificationFormNbn?.openCorrection();
    },
    forwardResume() {
      this.$refs.orderQualificationFormNbn?.onTriggerActivate();
    },
    forwardReversal() {
      this.$refs.orderQualificationFormNbn?.onTriggerServiceReversal();
    },
    forwardWithdraw() {
      this.$refs.orderQualificationFormNbn?.onTriggerCancel();
    },
    forwardRemoveConfiguration() {
      this.$refs.orderQualificationFormNbn?.onTriggerTransferLossRemove();
    },
  },
  watch: {
    details: function () {
      let vm = this;
      let hasLotNumber = false;

      if (vm.details.address.formattedAddress.includes("Lot"))
        hasLotNumber = true;

      let streetAddress = vm.details.address.formattedAddress.split(",");

      vm.$nextTick(() => {
        vm.$refs.qualification.result = {
          suburb: vm.details.address.suburb,
          state: vm.details.address.stateOrProvince,
          postCode: vm.details.address.postcode,
          streetAddress: hasLotNumber
            ? streetAddress[0] + ", " + streetAddress[1]
            : streetAddress[0],
        };
        vm.$refs.qualification.withData = vm.details.withData;
      });
    },
    locationID: function () {
      let vm = this;
      vm.newOrder = new Order();
      vm.resetProduct();
    },
    wholesalerAccount: function () {
      let vm = this;
      if (vm.wholesalerAccount) {
        if (
          typeof vm.$refs.orderDetails !== "undefined" &&
          vm.$refs.orderDetails.showMore
        )
          vm.updateCustomizedOffer({
            field: "Wholesaler",
            value: vm.wholesalerAccount,
          });
      }
    },
    wholesalerData: {
      immediate: true,
      handler(list) {
        let vm = this;
        if (!Array.isArray(list) || list.length === 0) return;

        list.forEach(x => { x.accountNumber = x.accountNumber == null ? null : Number(x.accountNumber); });

        if (vm.wholesalerAccount != null && vm.wholesalerAccount !== 0) {
          vm.wholesalerAccount = Number(vm.wholesalerAccount);
        }

        if (list.length === 1) {
          vm.wholesalerAccount = Number(list[0].accountNumber);
          vm.$nextTick(() => vm.wholesalerChanged(vm.wholesalerAccount));
          return;
        }

        const exists = list.some(x => Number(x.accountNumber) === Number(vm.wholesalerAccount));
        if (exists) {
          const v = Number(vm.wholesalerAccount);
          vm.wholesalerAccount = v;
        }
      }
    }
  },
  created(){
    window.addEventListener('beforeunload', this.handlePageRefresh)
    }
};
</script>

<style scoped>
::v-deep .custom-padding .v-banner__wrapper {
  padding-bottom: 0px !important;
}

.custom-title {
  font-size: 20px;
  font-weight: bold;
}

.rtl {
  direction: rtl;
}

.fixed-bar {
  width: 100%;
  position: sticky;
  bottom: -1px;
}

::v-deep
  .fixed-bar
  .theme--light.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
  background-color: #f5f5f5 !important;
}
</style>
